/**
 * Numerals policy (owner, 2026-10): customer-facing numbers use Western digits
 * with "," grouping — «1,250,000», never «١٬٢٥٠٬٠٠٠» — in BOTH languages.
 * Dates keep their calendar and only switch to Latin digits.
 *
 * Amounts go through ONE en-US formatter rather than an Arabic locale with
 * `-u-nu-latn`, because Arabic locales wrap minus and percent signs in invisible
 * direction marks (U+200E/U+200F) that break copy-paste and string compares.
 */
import { toLatinDigits } from './arabicNormalize';

const AMOUNT_LOCALE = 'en-US';

const amountFormatters = new Map<string, Intl.NumberFormat>();
const dateFormatters = new Map<string, Intl.DateTimeFormat>();

/** Order-independent cache key for an Intl options bag. */
function optionsKey(opts?: object): string {
  if (!opts) return '';
  const record = opts as Record<string, unknown>;
  return Object.keys(record)
    .filter((k) => record[k] !== undefined)
    .sort()
    .map((k) => `${k}=${String(record[k])}`)
    .join('|');
}

/**
 * The shared en-US number formatter for `opts`, created once and reused
 * (Intl.NumberFormat construction is the expensive part of formatting).
 */
export function getAmountFormatter(opts?: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = optionsKey(opts);
  let formatter = amountFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(AMOUNT_LOCALE, opts);
    amountFormatters.set(key, formatter);
  }
  return formatter;
}

/**
 * A finite number from a number or a numeric string (Arabic-Indic digits and ","
 * grouping accepted: "٢٥٠٠٠٠", "250,000"). Anything else → null.
 */
function toFiniteNumber(value: number | string | null | undefined): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const cleaned = toLatinDigits(value).replace(/[,\s]/g, '');
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/**
 * Western digits with "," grouping: formatAmount(1250000) → "1,250,000".
 * Precision follows Intl defaults (up to 3 decimals) unless `opts` say otherwise —
 * pass `{ maximumFractionDigits: 0 }` for whole riyals. Accepts digit strings
 * (as stored by filter inputs). Missing or non-numeric input → "" so the caller
 * can render its own honest empty state instead of "NaN".
 */
export function formatAmount(
  value: number | string | null | undefined,
  opts?: Intl.NumberFormatOptions,
): string {
  const n = toFiniteNumber(value);
  return n === null ? '' : getAmountFormatter(opts).format(n);
}

/**
 * A ratio as a percentage with Western digits: formatPercent(0.0617) → "6.2%".
 * `digits` is the maximum number of decimals (trailing zeros dropped).
 */
export function formatPercent(ratio: number | null | undefined, digits = 1): string {
  if (typeof ratio !== 'number' || !Number.isFinite(ratio)) return '';
  return getAmountFormatter({ style: 'percent', maximumFractionDigits: digits }).format(ratio);
}

/** The runtime's default locale (what `toLocaleString(undefined)` would use). */
function runtimeLocale(): string {
  try {
    return new Intl.DateTimeFormat().resolvedOptions().locale || AMOUNT_LOCALE;
  } catch {
    return AMOUNT_LOCALE;
  }
}

/**
 * The same locale with Latin digits forced, keeping its language, region and
 * calendar: latnLocale('ar-SA') → 'ar-SA-u-nu-latn'. An existing numbering
 * system is replaced. No locale → the runtime default; an invalid tag → 'en-US'.
 */
export function latnLocale(locale?: string | null): string {
  const base = (locale ?? '').trim().replace(/_/g, '-') || runtimeLocale();
  try {
    return new Intl.Locale(base, { numberingSystem: 'latn' }).toString();
  } catch {
    return AMOUNT_LOCALE;
  }
}

/**
 * A date in the given locale's language and calendar, with Latin digits.
 * Accepts a Date, an ISO string or a timestamp; missing or invalid → "".
 */
export function formatDateLatn(
  date: Date | string | number | null | undefined,
  locale?: string | null,
  opts?: Intl.DateTimeFormatOptions,
): string {
  if (date === null || date === undefined || date === '') return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const tag = latnLocale(locale);
  const key = `${tag}#${optionsKey(opts)}`;
  let formatter = dateFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(tag, opts);
    dateFormatters.set(key, formatter);
  }
  return formatter.format(d);
}
